import React, { useState } from 'react';
import { FiMessageCircle } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import chatService from '../../services/chat.service';

/**
 * ContactSellerButton
 *
 * Props:
 *   merchantId  (required) - seller's user id
 *   productId   (optional) - MongoDB product _id
 *   orderId     (optional) - MySQL order id
 *   storeName   (optional) - displayed in toast
 *   variant     (optional) - 'button' | 'icon' | 'link'
 *   onChatStarted (optional) - callback after chat is created
 */
const ContactSellerButton = ({
  merchantId,
  productId,
  orderId,
  storeName,
  variant = 'button',
  onChatStarted,
}) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (!isAuthenticated) {
      toast.info('Please log in to contact the seller');
      navigate('/login');
      return;
    }

    if (user?.id === merchantId) {
      toast.info("You can't message your own store");
      return;
    }

    setLoading(true);
    try {
      const res = await chatService.startConversation({
        merchantId,
        productId,
        orderId,
      });

      const convo = res.data.data;
      toast.success(res.data.resumed ? 'Chat resumed' : 'Chat started!');
      onChatStarted?.(convo);

      // Dispatch event so ChatWidget opens to this conversation
      window.dispatchEvent(new CustomEvent('open-chat', { detail: { conversationId: convo._id } }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start chat');
    }
    setLoading(false);
  };

  if (variant === 'icon') {
    return (
      <button
        onClick={handleClick}
        disabled={loading}
        title={`Chat with ${storeName || 'seller'}`}
        style={{
          background: 'none', border: '1px solid #E5E7EB', borderRadius: '8px',
          padding: '8px', cursor: 'pointer', color: '#B91C1C', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}
      >
        <FiMessageCircle size={18} />
      </button>
    );
  }

  if (variant === 'link') {
    return (
      <button
        onClick={handleClick}
        disabled={loading}
        style={{
          background: 'none', border: 'none', color: '#B91C1C', cursor: 'pointer',
          fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px',
          padding: 0, textDecoration: 'underline',
        }}
      >
        <FiMessageCircle size={14} /> {loading ? 'Opening...' : `Chat with ${storeName || 'Seller'}`}
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className="btn btn-outline-danger btn-sm"
      style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
    >
      <FiMessageCircle size={14} /> {loading ? 'Opening...' : 'Chat with Seller'}
    </button>
  );
};

export default ContactSellerButton;
